/**
 * Server audit trail → human action-log lines (ReportEditor right panel).
 *
 * GET /api/audit?entityType=art14_event&entityId=:id returns NEWEST-FIRST rows;
 * `buildLog` merges them with System milestones derived from the event's own
 * timestamps into one oldest-first log. Pure functions — see audit-log.test.ts.
 */
import { shortUtc } from './contract';

/** Row shape of GET /api/audit (subset the console consumes). */
export interface AuditRow {
  id: string;
  actor: string;
  action: string;
  afterSummary?: string | null;
  timestamp: string;
}

export interface LogLine {
  when: string;
  who: string;
  what: string;
}

/**
 * displayHandle-style actor: e-mails collapse to a capitalized local part
 * (zero-PII — the e-mail local part is the only handle we ever have); opaque
 * machine ids (uuid/cuid) are clipped so the log column stays readable.
 */
export function shortActor(actor: string): string {
  const a = actor.trim();
  if (!a || a.toLowerCase() === 'system') return 'System';
  if (a.includes('@')) {
    const local = a.split('@')[0] ?? '';
    return local ? local.charAt(0).toUpperCase() + local.slice(1) : a;
  }
  return a.length > 16 && !a.includes(' ') ? `${a.slice(0, 8)}…` : a;
}

const ACTION_PHRASES: Record<string, (after: string | null) => string> = {
  'art14.acknowledged': () => 'acknowledged reception',
  'art14.report_status': (a) => `status → ${a ?? '?'}`,
  // Legacy vocabulary written by the pre-existing services (source 'api').
  'art14.report_status_set': (a) => `status → ${a ?? '?'}`,
  'art14.fix_available_set': (a) =>
    a ? `corrective measure available ${a} — 14-day final clock started` : 'corrective measure available',
  'art14.stage_changed': (a) => `changed stage → ${a ?? '?'}`,
  'art14.users_informed': () => 'marked impacted users informed — Art.14(8)',
  'art14.fix_available': (a) =>
    a ? `corrective measure available ${a} — 14-day final clock started` : 'corrective measure available',
  'art14.notified': () => 'submitted 24h early warning to ENISA SRP',
  'art14.follow_up_submitted': () => 'submitted 72h detailed notification to ENISA SRP',
  'art14.final_report_submitted': () => 'submitted final report to ENISA SRP',
  'art14.package_generated': () => 'generated the ENISA SRP submission (manual fill guide + CSAF)',
};

/** Humanize an audit action (+ its afterSummary) into a log phrase. */
export function humanizeAction(action: string, afterSummary: string | null): string {
  const known = ACTION_PHRASES[action];
  if (known) return known(afterSummary);
  // Unknown/new action codes degrade gracefully: strip the prefix, de-snake.
  const base = action.replace(/^art14\./, '').replace(/[._]/g, ' ');
  return afterSummary ? `${base} → ${afterSummary}` : base;
}

/** One server audit row → one log line. */
export function auditToLogLine(row: AuditRow): LogLine {
  return {
    when: shortUtc(row.timestamp),
    who: shortActor(row.actor),
    what: humanizeAction(row.action, row.afterSummary ?? null),
  };
}

/** The event timestamps that yield derived System rows (subset of Art14Row). */
export interface EventLogTimestamps {
  detectedAt: string;
  acknowledgedAt: string | null;
  notifiedAt: string | null;
  followUpSubmittedAt?: string | null;
  usersInformedAt: string | null;
  finalReportSubmittedAt?: string | null;
}

interface DerivedRow {
  ts: string;
  what: string;
  /** Audit action that supersedes this derived row when the server logged it. */
  coveredBy: string | null;
}

function derivedRows(ev: EventLogTimestamps): DerivedRow[] {
  const rows: DerivedRow[] = [{ ts: ev.detectedAt, what: 'opened incident', coveredBy: null }];
  if (ev.acknowledgedAt) rows.push({ ts: ev.acknowledgedAt, what: 'acknowledged reception', coveredBy: 'art14.acknowledged' });
  if (ev.notifiedAt) rows.push({ ts: ev.notifiedAt, what: 'submitted 24h early warning', coveredBy: 'art14.notified' });
  if (ev.followUpSubmittedAt) rows.push({ ts: ev.followUpSubmittedAt, what: 'submitted 72h detailed notification', coveredBy: 'art14.follow_up_submitted' });
  if (ev.finalReportSubmittedAt) rows.push({ ts: ev.finalReportSubmittedAt, what: 'submitted final report', coveredBy: 'art14.final_report_submitted' });
  if (ev.usersInformedAt) rows.push({ ts: ev.usersInformedAt, what: 'impacted users informed — Art.14(8)', coveredBy: 'art14.users_informed' });
  return rows;
}

/**
 * Merge derived System milestones with the server audit rows (newest-first as
 * served) into ONE oldest-first log. A derived row is dropped when the audit
 * trail already carries the corresponding action (the server row wins — it has
 * the real actor); 'opened incident' has no audit action and is always kept.
 */
export function buildLog(ev: EventLogTimestamps, audit: AuditRow[]): LogLine[] {
  const auditActions = new Set(audit.map((r) => r.action));
  const derived = derivedRows(ev)
    .filter((d) => d.coveredBy === null || !auditActions.has(d.coveredBy))
    .map((d) => ({ ts: Date.parse(d.ts) || 0, line: { when: shortUtc(d.ts), who: 'System', what: d.what } }));
  const server = audit.map((r) => ({ ts: Date.parse(r.timestamp) || 0, line: auditToLogLine(r) }));
  return [...derived, ...server].sort((a, b) => a.ts - b.ts).map((x) => x.line);
}
